import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

/**
 * Bar Table Master — completely separate from KOT Table Master per user
 * directive 2026-04-24 (Bar is a totally separate module; do not link
 * with KOT). Reads/writes service_tables + bar_table_qr via
 * /api/v1/bar/masters/tables — mirrors the KOT admin UX for QR
 * generate/rotate but against the bar-side registry.
 *
 * Props:
 *   selectedPropertyId — property UUID (from BarMastersHub)
 *   selectedOutletId   — optional; used for QR generation (must be BAR)
 *
 * Outlet is needed for QR gen because the URL embeds outlet_code. The
 * screen auto-picks the first active BAR outlet for the property if
 * selectedOutletId isn't passed.
 */

const copyText = async (text) => {
  const s = String(text ?? '');
  try { if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(s); return true; } } catch {}
  try {
    const ta = document.createElement('textarea');
    ta.value = s; ta.setAttribute('readonly', '');
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

const qrImageSrc = (url, size = 280) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(url || '')}`;

export default function BarTableMaster({ token, selectedPropertyId = '', selectedOutletId = '' }) {
  const propertyId = selectedPropertyId || '';
  const [chainId, setChainId] = useState('');
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState(selectedOutletId || '');
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ table_name: '', capacity: 2, zone: '', is_active: true });
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [qrModal, setQrModal] = useState(null);
  const [qrCopied, setQrCopied] = useState(false);
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  // Resolve chain_id for the property (BarMastersHub only passes property id)
  useEffect(() => {
    if (!propertyId || !token) { setChainId(''); return; }
    (async () => {
      try {
        const r = await fetch('/api/properties/accessible', { headers: { Authorization: 'Bearer ' + token }});
        const data = await r.json().catch(() => []);
        const match = (Array.isArray(data) ? data : []).find(p => String(p.id) === String(propertyId));
        if (match?.chain_id) setChainId(match.chain_id);
      } catch { /* silent */ }
    })();
  }, [propertyId, token]);

  const scopeHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    'x-outlet-id': outletId,
    'Content-Type': 'application/json',
  });

  // Load BAR outlets only
  useEffect(() => {
    if (!chainId || !propertyId) return;
    (async () => {
      try {
        const r = await fetch(`/api/v1/core/outlets?property_id=${encodeURIComponent(propertyId)}`, {
          headers: { Authorization: 'Bearer ' + token, 'x-chain-id': chainId, 'x-property-id': propertyId },
        });
        const data = await r.json();
        if (r.ok) {
          const list = (Array.isArray(data) ? data : [])
            .filter(o => o.is_active !== false)
            .filter(o => String(o.outlet_type || '').toUpperCase() === 'BAR');
          setOutlets(list);
          if (!outletId && list.length) setOutletId(list[0].id);
        }
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, propertyId]);

  const loadRows = async () => {
    // Wait for chainId to resolve from the property lookup before firing —
    // backend's requireScope middleware rejects requests without x-chain-id.
    if (!propertyId || !chainId) { setRows([]); return; }
    try {
      const r = await fetch('/api/v1/bar/masters/tables?all=1', { headers: scopeHeaders() });
      const data = await r.json().catch(() => []);
      if (!r.ok) return setError(data.error || 'Failed to load bar tables');
      setError('');  // clear any prior race-condition error
      setRows(Array.isArray(data) ? data : []);
    } catch { setError('Network error while loading bar tables'); }
  };

  useEffect(() => { loadRows(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [propertyId, chainId, outletId]);

  const saveRow = async () => {
    setError(''); setMessage('');
    if (!String(form.table_name || '').trim()) return setError('Table name is required');
    try {
      const url = editingId ? `/api/v1/bar/masters/tables/${editingId}` : '/api/v1/bar/masters/tables';
      const r = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: scopeHeaders(),
        body: JSON.stringify({
          table_name: String(form.table_name).trim(),
          capacity: form.capacity === '' || form.capacity == null ? null : Number(form.capacity),
          zone: form.zone || null,
          is_active: form.is_active !== false,
        }),
      });
      const data = await r.json();
      if (!r.ok) return setError(data.error || 'Save failed');
      setMessage(editingId ? 'Bar table updated' : 'Bar table created');
      setEditingId('');
      setForm({ table_name: '', capacity: 2, zone: '', is_active: true });
      loadRows();
    } catch (e) { setError(e.message || 'Network error'); }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setForm({
      table_name: row.table_name || '',
      capacity: row.capacity ?? '',
      zone: row.zone || '',
      is_active: row.is_active !== false,
    });
    scrollToForm();
  };

  const deactivate = async (row) => {
    if (!window.confirm(`Deactivate "${row.table_name}"?`)) return;
    try {
      const r = await fetch(`/api/v1/bar/masters/tables/${row.id}`, { method: 'DELETE', headers: scopeHeaders() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); return setError(d.error || 'Deactivate failed'); }
      setMessage('Bar table deactivated');
      loadRows();
    } catch (e) { setError(e.message || 'Network error'); }
  };

  const openQrModal = async (row) => {
    setError(''); setMessage(''); setQrCopied(false);
    if (!outletId) { setError('Pick an active BAR outlet before generating QR.'); return; }
    setQrModal({ row, qr: null, loading: true, rotating: false });
    try {
      const r = await fetch(`/api/v1/bar/masters/tables/${row.id}/qr`, { headers: scopeHeaders() });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Failed to load QR'); setQrModal(null); return; }
      setQrModal({ row, qr: data.qr, loading: false, rotating: false });
    } catch (e) { setError(e.message || 'Network error'); setQrModal(null); }
  };

  const rotateQr = async () => {
    if (!qrModal?.row) return;
    setQrModal(m => ({ ...m, rotating: true }));
    try {
      const r = await fetch(`/api/v1/bar/masters/tables/${qrModal.row.id}/qr/rotate`, {
        method: 'POST', headers: scopeHeaders(),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Rotate failed'); setQrModal(m => ({ ...m, rotating: false })); return; }
      setQrCopied(false);
      setQrModal(m => ({ ...m, qr: data.qr, rotating: false }));
    } catch (e) { setError(e.message || 'Network error'); setQrModal(m => m ? { ...m, rotating: false } : null); }
  };

  const printQr = () => {
    if (!qrModal?.qr?.qr_url) return;
    const url = qrModal.qr.qr_url;
    const tableLabel = qrModal.row?.table_name || '';
    const outletLabel = outlets.find(o => o.id === outletId)?.name || '';
    const html = `
      <html><head><title>Bar Table QR — ${tableLabel}</title>
      <style>
        body { font-family: Arial, sans-serif; text-align: center; padding: 24px; }
        h1 { margin: 0 0 6px; font-size: 28px; }
        h2 { margin: 0 0 14px; font-weight: 500; color: #555; font-size: 16px; }
        .qr { padding: 10px; display: inline-block; border: 1px solid #ccc; }
        p { color: #666; font-size: 13px; margin-top: 14px; }
      </style></head>
      <body>
        <h1>${outletLabel}</h1>
        <h2>Scan to order — Table ${tableLabel}</h2>
        <div class="qr"><img src="${qrImageSrc(url, 360)}" width="360" height="360"/></div>
        <p>${url}</p>
      </body></html>`;
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(html); w.document.close();
    setTimeout(() => { w.print(); }, 400);
  };

  const inputStyle = { padding: '0.55rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem' };
  const btn = { padding: '0.42rem 0.8rem', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.84rem' };

  return (
    <div className="page-shell">
      {error && <div style={{ background: '#fef2f2', color: '#991b1b', padding: '0.5rem 0.8rem', borderRadius: 8, border: '1px solid #fecaca', marginBottom: '0.6rem' }}>{error}</div>}
      {message && <div style={{ background: '#ecfdf5', color: '#065f46', padding: '0.5rem 0.8rem', borderRadius: 8, border: '1px solid #a7f3d0', marginBottom: '0.6rem' }}>{message}</div>}

      <div className="filter-bar" style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ margin: '0 0 0.45rem', fontSize: '1rem' }}>Property & Bar Outlet (for QR URL)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.7rem' }}>
          <input readOnly value={propertyId ? `Property ${String(propertyId).slice(0,8)}…` : 'No property'} style={{ ...inputStyle, background: '#f1f5f9', width: '100%', boxSizing: 'border-box' }} />
          <select value={outletId} onChange={(e) => setOutletId(e.target.value)} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}>
            <option value="">Select BAR outlet</option>
            {outlets.map(o => <option key={o.id} value={o.id}>{o.code} — {o.outlet_name || o.name} (BAR)</option>)}
          </select>
        </div>
        {outlets.length === 0 && chainId && (
          <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.8rem', background: '#fef3c7', color: '#78350f', borderRadius: 6, fontSize: '0.82rem' }}>
            No active BAR outlets on this property. Activate a bar outlet first (KOT &amp; POS → Masters → Outlets).
          </div>
        )}
      </div>

      <h2 style={{ color: 'var(--theme-primary-dark)', marginTop: 0 }}>Bar Table Master</h2>

      <div ref={formRef} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '1rem', marginBottom: '0.8rem', ...editHighlightStyle(editHighlight) }}>
        <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit Bar Table' : 'Add Bar Table'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.7rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: '#475569', marginBottom: 4 }}>Table Name</label>
            <input value={form.table_name} onChange={(e) => setForm(f => ({ ...f, table_name: e.target.value }))} placeholder="e.g. Table 1, Bar Seat 3" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: '#475569', marginBottom: 4 }}>Capacity</label>
            <input type="number" min="1" value={form.capacity} onChange={(e) => setForm(f => ({ ...f, capacity: e.target.value }))} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: '#475569', marginBottom: 4 }}>Zone (optional)</label>
            <input value={form.zone} onChange={(e) => setForm(f => ({ ...f, zone: e.target.value }))} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))} />
            Active
          </label>
        </div>
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={saveRow} style={{ ...btn, background: '#1e40af', color: '#fff' }}>{editingId ? 'Save changes' : 'Add'}</button>
          {editingId && <button onClick={() => { setEditingId(''); setForm({ table_name: '', capacity: 2, zone: '', is_active: true }); }} style={{ ...btn, background: '#e2e8f0', color: '#334155' }}>Cancel</button>}
        </div>
      </div>

      <h3>Bar Tables</h3>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#1e40af', color: '#fff' }}>
              <th style={{ padding: '0.7rem', textAlign: 'left' }}>Table Name</th>
              <th style={{ padding: '0.7rem', textAlign: 'left' }}>Capacity</th>
              <th style={{ padding: '0.7rem', textAlign: 'left' }}>Zone</th>
              <th style={{ padding: '0.7rem', textAlign: 'left' }}>Active</th>
              <th style={{ padding: '0.7rem', textAlign: 'left' }}>QR</th>
              <th style={{ padding: '0.7rem', textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.7rem' }}>{row.table_name}</td>
                <td style={{ padding: '0.7rem' }}>{row.capacity ?? '-'}</td>
                <td style={{ padding: '0.7rem' }}>{row.zone ?? '-'}</td>
                <td style={{ padding: '0.7rem' }}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={{ padding: '0.7rem' }}>
                  <button onClick={() => openQrModal(row)} style={{ ...btn, background: '#6366f1', color: '#fff', padding: '0.35rem 0.7rem' }}>QR Code</button>
                </td>
                <td style={{ padding: '0.7rem' }}>
                  <button onClick={() => editRow(row)} style={{ ...btn, background: '#1e40af', color: '#fff', marginRight: 6 }}>Edit</button>
                  {row.is_active && <button onClick={() => deactivate(row)} style={{ ...btn, background: '#dc2626', color: '#fff' }}>Deactivate</button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b' }}>No bar tables yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* QR modal */}
      {qrModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.2rem', width: 'min(520px, 96vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
              <h3 style={{ margin: 0 }}>QR for {qrModal.row.table_name}</h3>
              <button onClick={() => setQrModal(null)} style={{ ...btn, background: '#e2e8f0', color: '#334155' }}>Close</button>
            </div>
            {qrModal.loading && <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading…</div>}
            {!qrModal.loading && !qrModal.qr && (
              <div style={{ padding: '1.5rem', textAlign: 'center' }}>
                <p style={{ color: '#475569' }}>No QR generated yet for this table.</p>
                <button onClick={rotateQr} disabled={qrModal.rotating} style={{ ...btn, background: '#1e40af', color: '#fff', padding: '0.6rem 1rem' }}>
                  {qrModal.rotating ? 'Generating…' : 'Generate QR'}
                </button>
              </div>
            )}
            {!qrModal.loading && qrModal.qr && (
              <div style={{ textAlign: 'center' }}>
                <img src={qrImageSrc(qrModal.qr.qr_url, 260)} alt="QR code" style={{ width: 260, height: 260, border: '1px solid #e2e8f0', borderRadius: 6, padding: 8 }} />
                <div style={{ margin: '0.6rem 0', fontSize: '0.78rem', color: '#475569', wordBreak: 'break-all' }}>{qrModal.qr.qr_url}</div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.8rem' }}>Code: <code>{qrModal.qr.qr_code}</code></div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={async () => { const ok = await copyText(qrModal.qr.qr_url); setQrCopied(ok); }} style={{ ...btn, background: '#0f766e', color: '#fff' }}>{qrCopied ? '✓ Copied' : 'Copy URL'}</button>
                  <button onClick={printQr} style={{ ...btn, background: '#1e40af', color: '#fff' }}>Print</button>
                  <button onClick={rotateQr} disabled={qrModal.rotating} style={{ ...btn, background: '#b91c1c', color: '#fff' }}>{qrModal.rotating ? 'Rotating…' : 'Rotate'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
